import { useState, useEffect, useMemo } from "react";
import { Badge, Menu, MenuItem, Popover, StatTile, SegmentedControl, Btn, Card, DataTable, Dropdown, FilterSelect, Icon, IconTile, InlineEmptyState, Input, Pill, SectionLabel, Spinner, T, TimeZonePicker, toneColors, type TableColumn } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useIsMobile } from "../../shell/useResponsive";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { FilterBar, RowActions } from "../../shell/composites";
import { SharedWithCoord } from "../../shell/list-composites";
import { useLocations, LOCATIONS_REQUEST_OPTS } from "./useLocations";
import { duplicateLocation, archiveLocation, updateLocation } from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { locationOpenNow, locationStatusMeta, type UILocation, type OpenState } from "./locations-adapter";
import { addressFormatFor, downloadText, IMPORT_FIELDS } from "./location-validation";
import { useT, t as i18nT } from "../../lib/i18n";

/**
 * Locations — list screen.
 *
 * Rebuilt 2026-08-07 (Nick) from `Locations - Admin & Bulk Creation.dc.html`
 * option 1a, for the System Administrator persona in all three verticals.
 * Three things the old list didn't do, all of which only matter at estate
 * scale (Maison Rivière alone spans six countries and five time zones):
 *
 *  · **Live local time per row.** "Is Tokyo open right now" is the question an
 *    administrator actually scans for, and it should not require arithmetic.
 *  · **Country facets** rather than one flat list, plus a "needs attention"
 *    queue (hours not set / no time zone / archived) that IS the work list.
 *  · **Real bulk actions** on a selection — set time zone, copy hours from
 *    another location, export, archive. Every one awaits its typed op.
 *
 * The cards view is kept: it predates this design and nothing asked for its
 * removal.
 */
const CARD_GRID = "repeat(auto-fill, minmax(280px, 1fr))";

/* Needs-attention facet tints. The canonical DS tone pairs (`toneColors`) rather
   than the hand-picked hexes this screen shipped with: the literals held ONE
   appearance, so a selected "No time zone" row stayed near-white in dark mode
   with light ink on it. Both halves of each pair invert with the theme. */
const WARN_TINT = toneColors("warning");
const DANGER_TINT = toneColors("danger");

function openPill(l: UILocation) {
  if (l.status !== "active") {
    const meta = locationStatusMeta(l.status);
    return <Pill tone={meta.tone} dot>{meta.label}</Pill>;
  }
  if (!l.tz) return <Pill tone="danger" dot>{i18nT("locations.list.noTimeZone", "No time zone")}</Pill>;
  const st: OpenState = locationOpenNow(l);
  if (st === "open") return <Pill tone="success" dot>{i18nT("locations.list.openNow", "Open now")}</Pill>;
  if (st === "closed") return <Pill tone="neutral" dot>{i18nT("locations.list.closed", "Closed")}</Pill>;
  return <Pill tone="warn" dot>{i18nT("locations.list.hoursNotSet", "Hours not set")}</Pill>;
}

const raw = (l: UILocation) => (l.raw ?? {}) as Record<string, unknown>;
const str = (l: UILocation, k: string) => String(raw(l)[k] ?? "");

function zoneClock(tz: string): string {
  if (!tz) return "—";
  try { return new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date()); }
  catch { return "—"; }
}
function zoneOffset(tz: string): string {
  if (!tz) return "";
  try {
    const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" })
      .formatToParts(new Date()).find((x) => x.type === "timeZoneName");
    return p ? p.value.replace("GMT", "UTC") : "";
  } catch { return ""; }
}

/** Needs-attention labels. Functions, not constants — `t()` must never be
 *  called at module scope (that freezes one locale for the session). */
const FLAG_LABEL = {
  hours: () => i18nT("locations.list.hoursNotSet", "Hours not set"),
  tz: () => i18nT("locations.list.noTimeZone", "No time zone"),
  archived: () => i18nT("locations.adapter.archived", "Archived"),
};

export default function LocationsList() {
  const t = useT();
  const nav = useNav();
  const { locations, loading, error, reload } = useLocations();
  const [view, setView] = useState<"list" | "cards">(() => { try { return (localStorage.getItem("locations:view") as "list" | "cards") || "list"; } catch { return "list"; } });
  const setViewPersist = (m: "list" | "cards") => { setView(m); try { localStorage.setItem("locations:view", m); } catch { /* noop */ } };
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState<string | null>(null);
  const [flag, setFlag] = useState<"hours" | "tz" | "archived" | null>(null);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [sel, setSel] = useState<Record<string, true>>({});
  const [busy, setBusy] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);
  const [, setTick] = useState(0);
  /* Below ~1100px the 198px facet rail leaves a 7-column table 446px to live in,
     which hid the live-clock column and the row actions behind an inner scroll
     (verifier, 2026-08-07). The facets become FilterBar dropdowns instead and
     the table gets the whole width back. */
  /* Fold tiers, measured against the real content box (viewport minus the rail
     and the 198px facet column), NOT guessed:
       > 1150px  six columns fit — full set.
       ≤ 1150px  Staff AND Time zone fold into the name cell. Six columns need
                 ~900px of minimums; the content box at 924px is 648px, so the
                 first cut of this pass pushed Actions 252px outside the wrapper
                 and the row buttons could only be reached by scrolling the
                 table sideways. A list whose actions are off-screen is broken.
       ≤ 760px   Address folds in as well — name + hours + actions.
     The rule is the same at every tier: fold into the name cell, never scroll. */
  const narrow = useIsMobile(1150);
  const phone = useIsMobile(760);

  /* The clocks are the reason this column earns its width — keep them true. */
  useEffect(() => {
    const h = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(h);
  }, []);

  const canWrite = can("locations.write");
  const staffAt = (l: UILocation) => (raw(l).staffCount as number | undefined) ?? 0;
  const resAt = (l: UILocation) => l.roomsCount;

  const facets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of locations) {
      const cc = str(l, "country").toUpperCase() || "—";
      counts.set(cc, (counts.get(cc) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([cc, n]) => ({ cc, n, name: cc === "—" ? i18nT("locations.list.noCountrySet", "No country set") : addressFormatFor(cc).name }))
      .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  }, [locations]);

  const flagCounts = useMemo(() => ({
    hours: locations.filter((l) => !l.hours.some((day) => day.windows.length)).length,
    tz: locations.filter((l) => !str(l, "timezone")).length,
    archived: locations.filter((l) => str(l, "status") === "archived").length,
  }), [locations]);

  const filtered = locations.filter((l) => {
    if (query.trim() && !`${l.name} ${l.address || ""} ${str(l, "externalCode")} ${str(l, "city")}`.toLowerCase().includes(query.toLowerCase())) return false;
    if (country && (str(l, "country").toUpperCase() || "—") !== country) return false;
    if (flag === "hours" && l.hours.some((day) => day.windows.length)) return false;
    if (flag === "tz" && str(l, "timezone")) return false;
    if (flag === "archived" && str(l, "status") !== "archived") return false;
    return true;
  });

  const sorted = (() => {
    if (!sortKey) return filtered;
    const num = sortKey === "staff" || sortKey === "resources";
    const val = (l: UILocation) => sortKey === "staff" ? staffAt(l) : sortKey === "resources" ? resAt(l) : sortKey === "tz" ? str(l, "timezone") : (l as unknown as Record<string, unknown>)[sortKey];
    return [...filtered].sort((a, b) => {
      const r = num ? (Number(val(a)) || 0) - (Number(val(b)) || 0) : String(val(a) || "").localeCompare(String(val(b) || ""));
      return sortDir === "desc" ? -r : r;
    });
  })();

  const selected = sorted.filter((l) => sel[l.id]);
  const selCount = selected.length;
  const clearSel = () => setSel({});
  const toggleSel = (id: string) => setSel((s) => { const n = { ...s }; if (n[id]) delete n[id]; else n[id] = true; return n; });

  /* ── Row actions (all awaited; no optimistic success) ─────────────────── */
  const dupLocation = async (l: UILocation) => {
    try { await duplicateLocation(l.id, LOCATIONS_REQUEST_OPTS); reload(); toast({ tone: "success", title: t("locations.list.locationDuplicated", "Location duplicated"), subtitle: i18nT("locations.list.aCopyOfLNameWasCreated.icu", { value0: l.name }) }); }
    catch (e) { toast({ tone: "danger", title: t("locations.list.couldnTDuplicate", "Couldn't duplicate"), subtitle: e instanceof Error ? e.message : String(e) }); }
  };
  const removeLocation = async (l: UILocation) => {
    try { await archiveLocation(l.id, { archived: true }, LOCATIONS_REQUEST_OPTS); reload(); toast({ tone: "warn", title: t("locations.list.locationArchived", "Location archived"), subtitle: t("locations.list.hiddenFromNewEventsExistingEventsKeepIt", "Hidden from new events. Existing events keep it.") }); }
    catch (e) { toast({ tone: "danger", title: t("locations.list.couldnTArchive", "Couldn't archive"), subtitle: e instanceof Error ? e.message : String(e) }); }
  };

  /* ── Bulk actions ─────────────────────────────────────────────────────── */
  const runBulk = async (label: string, fn: (l: UILocation) => Promise<unknown>) => {
    if (busy || !selCount) return;
    setBusy(true);
    let ok = 0;
    const failures: string[] = [];
    for (const l of selected) {
      try { await fn(l); ok++; }
      catch (e) { failures.push(`${l.name}: ${e instanceof Error ? e.message : String(e)}`); }
    }
    setBusy(false);
    reload();
    clearSel();
    if (!failures.length) toast({ tone: "success", title: label, subtitle: `${ok} ${ok === 1 ? t("locations.list.location", "location") : t("locations.list.locations", "Locations").toLowerCase()}` });
    else if (!ok) toast({ tone: "danger", title: t("locations.list.nothingChanged", "Nothing changed"), subtitle: failures[0] });
    else toast({ tone: "warn", title: `${label} — ${ok} ${t("locations.list.ofN", "of")} ${ok + failures.length}`, subtitle: failures[0] });
  };

  const bulkTimeZone = (tz: string) => {
    setTzOpen(false);
    void runBulk(t("locations.list.timeZoneUpdated", "Time zone updated"), (l) =>
      (updateLocation as unknown as (id: string, b: unknown, o: unknown) => Promise<unknown>)(l.id, { timezone: tz }, LOCATIONS_REQUEST_OPTS));
  };

  const bulkCopyHours = (sourceId: string) => {
    const src = locations.find((l) => l.id === sourceId);
    if (!src) return;
    const hours = raw(src).hours;
    if (!Array.isArray(hours) || !hours.length) {
      toast({ tone: "warn", title: t("locations.list.thatLocationHasNoHours", "That location has no hours to copy"), subtitle: src.name });
      return;
    }
    void runBulk(t("locations.list.hoursCopied", "Hours copied"), (l) =>
      (updateLocation as unknown as (id: string, b: unknown, o: unknown) => Promise<unknown>)(l.id, { hours, hoursSet: true }, LOCATIONS_REQUEST_OPTS));
  };

  const bulkArchive = () => void runBulk(t("locations.list.locationsArchived", "Locations archived"), (l) =>
    archiveLocation(l.id, { archived: true }, LOCATIONS_REQUEST_OPTS));

  const exportRows = (list: UILocation[]) => {
    const esc = (s: string) => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const cols = IMPORT_FIELDS.map((f) => f.key);
    const lines = [IMPORT_FIELDS.map((f) => f.label).join(",")];
    for (const l of list) lines.push(cols.map((c) => esc(str(l, c))).join(","));
    downloadText("locations.csv", lines.join("\r\n"));
    toast({ tone: "success", title: t("locations.list.exported", "Exported"), subtitle: `${list.length} ${t("locations.list.rows", "rows")}` });
  };

  const actions = (l: UILocation) => ({
    testid: "locations-item",
    onView: () => nav.go("locations.edit", { id: l.id, view: 1 }),
    viewLabel: "View details",
    onEdit: canWrite ? () => nav.go("locations.edit", { id: l.id }) : undefined,
    editLabel: "Edit location",
    onDuplicate: canWrite ? () => void dupLocation(l) : undefined,
    moreHeader: l.name,
    moreItems: canWrite ? [{ icon: <Icon.trash size={14} />, label: t("locations.list.archiveLocation", "Archive location"), danger: true, sub: "Hides from new events", onClick: () => void removeLocation(l) }] : [],
  });

  /* Columns for the DS `DataTable` (2026-09-11 review). This screen used to
     hand-roll <table>/<thead>/<tbody> with cell chrome imported from EventCard
     and its own chevron sort, on the argument that Table could not reproduce it
     byte-for-byte. Operator ruling: follow the design system. The cells below
     are the same content; padding, borders, sort icon, selection tint and the
     sticky header now come from the DS.

     RESPONSIVE: the column SET changes with width rather than the table
     scrolling sideways. Staff folds into the name cell below 1100px (as
     before); below 760px Address and Time zone fold in too, because a phone
     cannot show six columns and those two are why people open this list. */
  const columns: TableColumn<UILocation>[] = [
    {
      id: "name",
      header: t("locations.list.location", "Location"),
      sortable: true,
      minWidth: phone ? 180 : 200,
      cell: (l: UILocation) => {
        const tz = str(l, "timezone");
        const place = [str(l, "city"), str(l, "region")].filter(Boolean).join(", ");
        return (
          <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
            <IconTile icon={<Icon.loc size={14} />} tone="primary" />
            <div style={{ minWidth: 0 }}>
              <Btn variant="link" size="sm" style={{ paddingInline: 0, justifyContent: "flex-start" }} onClick={() => actions(l).onView()}>{l.name}</Btn>
              <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1, display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                {str(l, "externalCode") ? <span style={{ fontFamily: T.fontMono }}>{str(l, "externalCode")}</span> : null}
                {narrow ? <span>{staffAt(l)} {t("locations.list.staff", "Staff").toLowerCase()}</span> : null}
              </div>
              {narrow ? (
                <div style={{ fontSize: 11.5, color: T.soft, marginTop: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                  {phone ? <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{[str(l, "address"), place].filter(Boolean).join(" · ") || l.address}</span> : null}
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>{tz ? `${zoneClock(tz)} ${zoneOffset(tz)} · ${tz}` : t("locations.list.noTimeZone", "No time zone")}</span>
                </div>
              ) : null}
            </div>
          </div>
        );
      },
    },
    ...(phone ? [] : [{
      id: "address",
      header: t("locations.list.address", "Address"),
      sortable: true,
      minWidth: 180,
      cell: (l: UILocation) => (
        <div style={{ color: T.n2, minWidth: 0 }}>
          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[str(l, "address"), str(l, "addressLine2")].filter(Boolean).join(", ") || l.address}</div>
          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[str(l, "city"), str(l, "region"), str(l, "postalCode")].filter(Boolean).join(", ")}
            {str(l, "country") ? ` · ${str(l, "country")}` : ""}
          </div>
        </div>
      ),
    } as TableColumn<UILocation>]),
    ...(narrow ? [] : [{
      id: "tz",
      header: t("locations.list.timeZoneLocalNow", "Time zone · local now"),
      sortable: true,
      minWidth: 168,
      cell: (l: UILocation) => {
        const tz = str(l, "timezone");
        return tz ? (
          <>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1, fontVariantNumeric: "tabular-nums" }}>{zoneClock(tz)}</span>
              <span style={{ fontSize: 11, fontFamily: T.fontMono, color: T.soft }}>{zoneOffset(tz)}</span>
            </div>
            <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1 }}>{tz}</div>
          </>
        ) : <Pill tone="danger" dot>{t("locations.list.noTimeZone", "No time zone")}</Pill>;
      },
    } as TableColumn<UILocation>]),
    {
      id: "hours",
      header: t("locations.list.hours", "Hours"),
      minWidth: 100,
      cell: (l: UILocation) => openPill(l),
    },
    ...(narrow ? [] : [{
      id: "staff",
      header: t("locations.list.staff", "Staff"),
      sortable: true,
      minWidth: 64,
      cell: (l: UILocation) => <span style={{ color: T.n1 }}>{staffAt(l)}</span>,
    } as TableColumn<UILocation>]),
    {
      id: "actions",
      header: t("locations.list.actions", "Actions"),
      align: "right" as const,
      minWidth: 76,
      cell: (l: UILocation) => <span onClick={(e) => e.stopPropagation()}><RowActions {...actions(l)} /></span>,
    },
  ];

  const openCount = locations.filter((l) => locationOpenNow(l) === "open").length;
  const zoneCount = new Set(locations.map((l) => str(l, "timezone")).filter(Boolean)).size;

  const facetRow = (key: string, label: string, n: number, on: boolean, onClick: () => void, tint?: { bg: string; fg: string; dot: string }) => (
    <Btn key={key} variant="ghost" size="sm" full active={on} onClick={onClick}
      style={{ justifyContent: "space-between", ...(tint && n > 0 ? {
        background: tint.bg, color: tint.fg, fontWeight: 600,
        borderColor: on ? tint.fg : "transparent",
      } : {}) }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}>
        {tint ? <Badge dot tone={n === 0 ? "neutral" : key === "tz" ? "danger" : key === "archived" ? "info" : "warning"} /> : null}
        {label}
      </span>
      <Badge count={n} max={99999} showZero tone={tint && n > 0 ? key === "tz" ? "danger" : key === "archived" ? "info" : "warning" : on ? "primary" : "neutral"} />
    </Btn>
  );

  return (
    <Screen rail="locations">
      <PageHeader
        title={t("locations.list.locations", "Locations")}
        subtitle={i18nT("locations.list.venuesSubtitleShort", "Hours, time zone and who works at each venue.")}
        breadcrumb={null}
        actions={<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <SegmentedControl<"list" | "cards">
            size="md"
            ariaLabel="View"
            value={view}
            onChange={setViewPersist}
            options={[
              { value: "list", label: t("locations.list.list", "≡ List") },
              { value: "cards", label: t("locations.list.cards", "⊞ Cards") },
            ]}
          />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("locations.edit", { new: 1 })}>
            {t("locations.list.addLocation", "Add location")}
          </Btn> : null}
          <Menu align="end" portal aria-label={t("locations.list.moreWaysToAdd", "More ways to add")}
            trigger={<Btn variant="secondary" aria-label={t("locations.list.moreWaysToAdd", "More ways to add")} iconOnly={<Icon.chev dir="down" size={14} />} />}>
            {canWrite ? <MenuItem icon={<Icon.upload size={14} />} onSelect={() => nav.go("locations.import")}>
              {t("locations.list.addInBulk", "Add in bulk")}
            </MenuItem> : null}
            {canWrite ? <MenuItem icon={<Icon.copy size={14} />} onSelect={() => nav.go("locations.import")}>
              {t("locations.list.cloneALocation", "Clone a location")}
            </MenuItem> : null}
            <MenuItem icon={<Icon.download size={14} />} onSelect={() => exportRows(sorted)}>
              {t("locations.list.exportCsv", "Export CSV")}
            </MenuItem>
          </Menu>
        </div>}
      />

      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 12 }}>
        <StatTile label={t("locations.list.locations", "Locations")} value={locations.length} icon={<Icon.loc size={14} />} accent={T.p1} />
        <StatTile label={t("locations.list.openRightNow", "Open right now")} value={openCount} icon={<Icon.clock size={14} />} accent={T.green1} />
        <StatTile label={t("locations.list.countries", "Countries")} value={facets.length} icon={<Icon.grid size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("locations.list.timeZones", "Time zones")} value={zoneCount} icon={<Icon.clock size={14} />} accent={T.p1} />
      </div>

      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("locations.list.searchNameCityOrCode", "Search name, city, or store code…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 260 }} />
        {query.trim() || country || flag ? (
          <Btn variant="link" size="sm" onClick={() => { setQuery(""); setCountry(null); setFlag(null); }}>{t("common.actions.clear", "Clear")}</Btn>
        ) : null}
        {narrow ? (
          <>
            <FilterSelect
              label={country ? (facets.find((f) => f.cc === country)?.name ?? country) : t("locations.list.anyCountry", "Any country")}
              options={facets.map((f) => `${f.name} (${f.n})`)}
              active={!!country}
              onPick={(o) => { const name = o.replace(/ \(\d+\)$/, ""); setCountry(facets.find((f) => f.name === name)?.cc ?? null); }}
              onClear={() => setCountry(null)}
              anyLabel={i18nT("locations.list.anyCountry", "Any country")}
            />
            <FilterSelect
              label={flag ? FLAG_LABEL[flag]() : t("locations.list.anyState", "Any state")}
              options={[`${FLAG_LABEL.hours()} (${flagCounts.hours})`, `${FLAG_LABEL.tz()} (${flagCounts.tz})`, `${FLAG_LABEL.archived()} (${flagCounts.archived})`]}
              active={!!flag}
              onPick={(o) => {
                const name = o.replace(/ \(\d+\)$/, "");
                setFlag(name === FLAG_LABEL.hours() ? "hours" : name === FLAG_LABEL.tz() ? "tz" : "archived");
              }}
              onClear={() => setFlag(null)}
              anyLabel={i18nT("locations.list.anyState", "Any state")}
            />
          </>
        ) : null}
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10 }}>
          <SharedWithCoord object="location list" note="Event Day's location picker reads the same locationId set." />
          <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>{sorted.length} {t("locations.list.of", "of")}{" "}{locations.length}</span>
        </span>
      </FilterBar>

      <div style={{ flex: 1, minHeight: 0, display: "flex", gap: 16, padding: phone ? "0 12px 16px" : "0 24px 24px" }}>
        {/* Facets — wide viewports only; below 1100px they ride in the FilterBar. */}
        {locations.length && !error && !narrow ? (
          <div style={{ width: 198, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            <Card style={{ padding: "10px 6px" }}>
              <SectionLabel style={{ padding: "0 10px 6px" }}>{t("locations.list.country", "Country")}</SectionLabel>
              {facets.map((f) => facetRow(f.cc, f.name, f.n, country === f.cc, () => setCountry(country === f.cc ? null : f.cc)))}
            </Card>
            <Card style={{ padding: "10px 6px" }}>
              <SectionLabel style={{ padding: "0 10px 6px" }}>{t("locations.list.needsAttention", "Needs attention")}</SectionLabel>
              {facetRow("hours", t("locations.list.hoursNotSet", "Hours not set"), flagCounts.hours, flag === "hours", () => setFlag(flag === "hours" ? null : "hours"), { bg: WARN_TINT.bg, fg: WARN_TINT.fg, dot: T.orange1 })}
              {facetRow("tz", t("locations.list.noTimeZone", "No time zone"), flagCounts.tz, flag === "tz", () => setFlag(flag === "tz" ? null : "tz"), { bg: DANGER_TINT.bg, fg: DANGER_TINT.fg, dot: T.red1 })}
              {facetRow("archived", t("locations.adapter.archived", "Archived"), flagCounts.archived, flag === "archived", () => setFlag(flag === "archived" ? null : "archived"), { bg: toneColors("info").bg, fg: toneColors("info").fg, dot: T.blue1 })}
            </Card>
          </div>
        ) : null}

        <div style={{ flex: 1, minWidth: 0, overflow: "auto" }}>
          {error ? (
            <InlineEmptyState icon={<IconTile icon={<Icon.warn size={20} />} tone="danger" />} title={t("locations.list.couldnTLoadLocations", "Couldn't load locations")} description={error} action={<Btn variant="secondary" onClick={reload}>{t("common.actions.retry", "Retry")}</Btn>} />
          ) : loading ? (
            /* DS skeleton for the table; the card grid keeps the text line. */
            view === "list"
              ? <DataTable<UILocation> columns={columns} rows={[]} getRowId={(l) => l.id} loading loadingRows={6} />
              : <div style={{ padding: 40, textAlign: "center" }}><Spinner label={t("locations.list.loadingLocations", "Loading locations…")} /></div>
          ) : sorted.length === 0 ? (
            <InlineEmptyState icon={<IconTile icon={<Icon.loc size={20} />} tone="primary" />} title={t("locations.list.noLocationsMatch", "No locations match")} description={t("locations.list.tryADifferentSearchOrAddALocation", "Try a different search, or add a location.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("locations.edit", { new: 1 })}>{t("locations.list.addLocation", "Add location")}</Btn> : null} />
          ) : view === "cards" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
              {sorted.map((l) => (
                <Card key={l.id} dataId={l.id} onClick={() => actions(l).onView()} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden" }}>
                  <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <IconTile icon={<Icon.loc size={17} />} tone="primary" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</div>
                        <div style={{ fontSize: T.text.label.fontSize, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.address || "No address"}</div>
                      </div>
                    </div>
                    <div>{openPill(l)}</div>
                    <div style={{ marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", gap: 16, fontSize: T.text.label.fontSize, color: T.soft }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.clock size={12} /> <b style={{ color: T.n1, fontVariantNumeric: "tabular-nums" }}>{zoneClock(str(l, "timezone"))}</b> {zoneOffset(str(l, "timezone"))}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.grid size={12} /> <b style={{ color: T.n1 }}>{resAt(l)}</b> {resAt(l) === 1 ? t('locations.list.text') : t('locations.list.text2')}</span>
                    </div>
                  </div>
                  <div style={{ borderTop: `1px solid ${T.border}`, padding: "8px 12px", display: "flex", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    <RowActions {...actions(l)} />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <DataTable<UILocation>
              testid="locations-table"
              columns={columns}
              rows={sorted}
              getRowId={(l) => l.id}
              stickyHeader
              selectedIds={new Set(selected.map((location) => location.id))}
              onRowSelect={(id) => toggleSel(id)}
              onSelectAll={(next) => setSel(next ? Object.fromEntries(sorted.map((l) => [l.id, true as const])) : {})}
              selectionNoun={selCount === 1 ? t("locations.list.locationSelected", "location selected") : t("locations.list.locationsSelected", "locations selected")}
              sort={sortKey ? { columnId: sortKey, direction: sortDir } : undefined}
              onSortChange={(next) => { setSortKey(next.columnId); setSortDir(next.direction); }}
              bulkActions={
                /* The bulk controls only. Count, select-all and Clear come from
                   DataTable's own toolbar — this screen used to draw all three
                   itself, including a bare <button> Clear under a raw-element
                   exemption that no longer applies. Wraps on narrow viewports. */
                <span style={{ display: "inline-flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                  <span style={{ position: "relative" }}>
                    <Btn size="sm" variant="secondary" disabled={busy} icon={<Icon.clock size={12} />} onClick={() => setTzOpen((v) => !v)}>
                      {t("locations.list.setTimeZone", "Set time zone")}
                    </Btn>
                    <Popover open={tzOpen} onClose={() => setTzOpen(false)} align="left" width={300}>
                        <span style={{ display: "block", fontSize: T.text.label.fontSize, color: T.soft, marginBottom: 7 }}>
                          {t("locations.list.appliedToEverySelectedLocation", "Applied to every selected location — and to everyone who signs in there.")}
                        </span>
                        <TimeZonePicker value="" onChange={(v: string) => bulkTimeZone(v)} />
                    </Popover>
                  </span>

                  {/* ACTION picker, not a field: `value={null}` fires and returns
                      to its placeholder. `size="sm"` matches the 28px buttons. */}
                  <Dropdown
                    testid="locations-bulk-copyhours"
                    size="sm"
                    disabled={busy}
                    value={null}
                    onChange={(id) => bulkCopyHours(id)}
                    placeholder={i18nT("locations.list.copyHoursFrom", "Copy hours from…")}
                    aria-label={i18nT("locations.list.copyHoursFrom", "Copy hours from…")}
                    style={{ width: phone ? 150 : 200, maxWidth: 200 }}
                    options={locations
                      .filter((l) => Array.isArray(raw(l).hours) && (raw(l).hours as unknown[]).length)
                      .map((l) => ({ value: l.id, label: l.name }))}
                  />

                  <Btn size="sm" variant="secondary" disabled={busy} icon={<Icon.download size={12} />} onClick={() => exportRows(selected)}>{t("common.actions.export", "Export")}</Btn>
                  <Btn size="sm" variant="secondary" disabled={busy} icon={<Icon.trash size={12} />} onClick={bulkArchive}>{t("locations.list.archive", "Archive")}</Btn>
                </span>
              }
            />
          )}
        </div>
      </div>
    </Screen>
  );
}
